import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { AuthForm, useAuth } from '../auth';
import { roleLabel, ROLE_OPTIONS, scopeLabel } from '../components/ShareDialog';
import type { AcceptedInvite, InvitePreview } from '../types';

/**
 * /invite/:token — someone shared memories. Shows what and at which level;
 * signed-out visitors sign in or create an account right here, then accept.
 */
export default function InvitePage() {
  const { token = '' } = useParams();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .invite(token)
      .then(setInvite)
      .catch((err) => setError((err as Error).message));
  }, [token]);

  const open = (r: AcceptedInvite) => {
    if (r.scope === 'EPISODE' && r.episodeId) navigate(`/episode/${r.episodeId}`, { replace: true });
    else if (r.scope === 'SEASON' && r.seasonYear) navigate(`/season/${r.seasonYear}?owner=${r.ownerId}`, { replace: true });
    else navigate('/', { replace: true });
  };

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      open(await api.acceptInvite(token));
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const what = invite && scopeLabel(invite);

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-ink-950 px-4 py-16">
      <Link to="/" className="focus-ring mb-8 flex items-center gap-2.5 rounded">
        <span className="text-accent text-4xl leading-none font-extrabold tracking-tight">M</span>
        <span className="text-2xl font-bold tracking-tight">
          Memory<span className="text-white/55">flix</span>
        </span>
      </Link>

      {!invite && !error && <p className="text-sm text-white/50">Opening the invite…</p>}

      {error && !invite && (
        <div className="max-w-sm text-center">
          <h1 className="mb-2 text-xl font-bold">This invite can’t be opened</h1>
          <p className="text-sm text-white/55">{error}</p>
        </div>
      )}

      {invite && (
        <div className="w-full max-w-sm">
          <div className="mb-6 rounded-xl bg-white/[0.05] p-5 text-center ring-1 ring-white/10">
            <p className="text-sm text-white/55">{invite.owner.name} shared</p>
            <h1 className="mt-1 text-xl font-bold">{what}</h1>
            <p className="mt-2 text-sm text-white/70">
              {roleLabel(invite.role)}: {ROLE_OPTIONS.find((o) => o.value === invite.role)?.help}
            </p>
            {invite.forEmail && <p className="mt-2 text-xs text-white/40">For {invite.forEmail}</p>}
          </div>

          {user ? (
            <div className="text-center">
              <button
                onClick={accept}
                disabled={busy}
                className="focus-ring w-full rounded-md bg-accent px-5 py-3 text-sm font-bold text-white hover:bg-accent-soft disabled:opacity-50"
              >
                {busy ? 'Opening…' : 'Accept and open'}
              </button>
              <p className="mt-3 text-xs text-white/45">
                Signed in as {user.email}.{' '}
                <button onClick={() => void signOut()} className="focus-ring underline underline-offset-2 hover:text-white">
                  Use another account
                </button>
              </p>
              {error && (
                <p role="alert" className="mt-3 text-sm text-red-300">
                  {error}
                </p>
              )}
            </div>
          ) : (
            <AuthForm invite={token} onAccepted={open} intro={<p className="mb-4 text-center text-sm text-white/55">Sign in or create an account to open it.</p>} />
          )}
        </div>
      )}
    </div>
  );
}
